import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { allDevicesQueryOptions } from "../../../features/devices/queries";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import { behaviorPointsOf, useReportBehaviorStore } from "../../../store/reports/reportBehaviorStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { aggregateByDriver } from "../utils/behaviorByDriver";
import { scoreByWeek } from "../utils/scoreByWeek";
import { downloadVehicleReportPdf } from "../utils/vehicleReportPdf";
import { fetchMonthlyBehavior } from "../utils/behaviorMonthlyApi";
import BehaviorEventsMap from "../components/BehaviorEventsMap";
import ReportPointsTable, { type ReportTableColumn } from "../components/ReportPointsTable";
import { ReportStatCard } from "../components/ReportCharts";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { toLocalInputValue, makeDateTimeFormatter, resolveTz } from "../utils/reportDateFormat";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import { DEFAULT_SETTINGS, PARAM_KEYS, gradeOf, type DrivingEvent, type ParamKey, type ScoreSettings } from "../utils/drivingScore";
import { fetchScoreSettings, saveScoreSettings, settingsErrorMessage } from "../utils/scoreSettingsApi";
import { fmtDuration } from "../../quick-reports/quickReports.compute";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import BehaviorSettingsModal from "./BehaviorSettingsModal";
import { driverDisplayName } from "../../../utils/driverName";

// Se lee el historial en el navegador (como Temperatura, Voltaje y Calidad
// GPS); el tope es el mismo.
const MAX_RANGE_DAYS = 31;
const DAY_MS = 24 * 60 * 60 * 1000;

export const PARAM_LABEL: Record<ParamKey, string> = {
  eco: "Eco velocidad",
  idle: "Ralentí",
  harshBrake: "Frenados bruscos",
  harshAccel: "Aceleraciones bruscas",
  harshTurn: "Giros bruscos",
  speeding: "Excesos",
};

const EVENT_LABEL: Record<DrivingEvent["kind"], string> = {
  harshBrake: "Frenado brusco",
  harshAccel: "Aceleración brusca",
  harshTurn: "Giro brusco",
  speeding: "Exceso de velocidad",
  idle: "Ralentí excesivo",
};

// Mismo orden de colores que Mapon: verde (A) a rojo oscuro (G).
const GRADE_CLASS: Record<string, string> = {
  A: "bg-green-600 text-white",
  B: "bg-lime-500 text-white",
  C: "bg-yellow-400 text-gray-900",
  D: "bg-amber-500 text-white",
  E: "bg-orange-500 text-white",
  F: "bg-red-500 text-white",
  G: "bg-red-800 text-white",
  "—": "bg-gray-300 text-gray-700",
};

function Grade({ score, size = "sm" }: { score: number | null; size?: "sm" | "lg" }) {
  const g = gradeOf(score);
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md font-bold ${GRADE_CLASS[g]} ${size === "lg" ? "w-10 h-10 text-xl" : "w-6 h-6 text-xs"}`}
      title={score == null ? "El equipo no manda este dato" : `${score.toFixed(1)} de 100`}
    >
      {g}
    </span>
  );
}

interface ExportRow {
  id: string;
  unidad: string;
  general: string;
  eco: string;
  ralenti: string;
  frenados: string;
  aceleraciones: string;
  giros: string;
  excesos: string;
  distancia: string;
  manejo: string;
}

const EXPORT_COLUMNS: ReportTableColumn<ExportRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "general", header: "Calificación" },
  { key: "eco", header: "Eco velocidad" },
  { key: "ralenti", header: "Ralentí" },
  { key: "frenados", header: "Frenados bruscos" },
  { key: "aceleraciones", header: "Aceleraciones bruscas" },
  { key: "giros", header: "Giros bruscos" },
  { key: "excesos", header: "Excesos" },
  { key: "distancia", header: "Distancia", align: "right" },
  { key: "manejo", header: "Tiempo de manejo", align: "right" },
];

interface EventRow {
  id: string;
  ts: number; // para centrar el mapa al tocar la fila (no es columna)
  unidad: string;
  evento: string;
  fecha: string;
  detalle: string;
  ubicacion: string;
}

const EVENT_COLUMNS: ReportTableColumn<EventRow>[] = [
  { key: "unidad", header: "Unidad" },
  { key: "evento", header: "Evento" },
  { key: "fecha", header: "Fecha y hora" },
  { key: "detalle", header: "Detalle" },
  { key: "ubicacion", header: "Ubicación" },
];

const fmtParam = (k: ParamKey, value: number) =>
  k === "eco" || k === "idle" || k === "speeding" ? `${value.toFixed(1)} %` : `${value}x`;

export default function BehaviorSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();
  const { showAlert } = useAlert();

  const loadRange = useReportBehaviorStore((s) => s.loadRange);
  const rescore = useReportBehaviorStore((s) => s.rescore);
  const clear = useReportBehaviorStore((s) => s.clear);
  const resultsState = useReportBehaviorStore((s) => s.results);
  const order = useReportBehaviorStore((s) => s.order);
  const loading = useReportBehaviorStore((s) => s.loading);
  const tz = useReportBehaviorStore((s) => s.tz);

  const [settings, setSettings] = useState<ScoreSettings>(DEFAULT_SETTINGS);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [kinds, setKinds] = useState<Set<DrivingEvent["kind"]>>(new Set(["harshBrake", "harshAccel", "harshTurn", "speeding", "idle"]));
  const [focusUnit, setFocusUnit] = useState<string | null>(null);
  const [mapTs, setMapTs] = useState<number | null>(null);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  // Por vehículos o por conductores, como las dos pestañas de Mapon.
  const [view, setView] = useState<"vehicles" | "drivers">("vehicles");
  const companies = useCompaniesStore((s) => s.allCompanies);

  const { control, setValue, watch, getValues, register, formState: { errors } } = useForm<{
    devices: string[];
    startDate: string;
    endDate: string;
  }>({ mode: "onBlur", defaultValues: { devices: [], startDate: "", endDate: "" } });

  const { companyId, disableWithoutCompany } = useReportCompanyScope(setValue, () => clear());
  const { data: allDevices } = useQuery({ ...allDevicesQueryOptions(companyId) });
  const settingsQuery = useQuery({
    queryKey: ["driving-score-settings", companyId],
    queryFn: () => fetchScoreSettings(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (settingsQuery.data) setSettings(settingsQuery.data.settings);
  }, [settingsQuery.data]);

  const selectedDevices = watch("devices");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const rangeDays = startDate && endDate ? (new Date(endDate).getTime() - new Date(startDate).getTime()) / DAY_MS : -1;
  const ready = (selectedDevices?.length ?? 0) > 0 && rangeDays >= 0 && rangeDays <= MAX_RANGE_DAYS;

  const nameById = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    (allDevices ?? []).forEach((d: any) => {
      map[d.id] = d.vehicle?.name || d.identifier || d.id;
    });
    return map;
  }, [allDevices]);

  const onView = async () => {
    const { devices, startDate: from, endDate: to } = getValues();
    const names = nameById();
    setFocusUnit(null);
    await loadRange(
      devices.map((id) => ({ deviceId: id, vehicleName: names[id] || id })),
      new Date(from).toISOString(),
      new Date(to).toISOString(),
      settings,
    );
  };

  const onSaveSettings = async (next: ScoreSettings) => {
    try {
      const saved = settingsQuery.data?.available === false ? next : await saveScoreSettings(companyId!, next);
      setSettings(saved);
      rescore(saved);
      setSettingsOpen(false);
      if (settingsQuery.data?.available === false) {
        showAlert({ title: "Ajustes aplicados", text: settingsErrorMessage({ response: { status: 404 } }), icon: "info", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
      } else {
        settingsQuery.refetch();
      }
    } catch (e) {
      showAlert({ title: "No se guardaron los ajustes", text: settingsErrorMessage(e), icon: "error", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
    }
  };

  const results = useMemo(
    () =>
      order
        .map((id) => resultsState[id])
        .filter((r) => r?.score)
        .sort((a, b) => (b.score!.overall ?? -1) - (a.score!.overall ?? -1)),
    [order, resultsState],
  );
  const rated = results.filter((r) => r.score!.overall != null);

  // Conductor asignado HOY a cada equipo (devices → driver); ver behaviorByDriver.
  const driverByDevice = useMemo(() => {
    const m: Record<string, { id?: string; name?: string } | null> = {};
    (allDevices ?? []).forEach((d: any) => {
      m[d.id] = d.driver?.id ? { id: d.driver.id, name: driverDisplayName(d.driver) ?? undefined } : null;
    });
    return m;
  }, [allDevices]);
  const driverRows = useMemo(
    () => aggregateByDriver(results.map((r) => ({ deviceId: r.deviceId, vehicleName: r.vehicleName, score: r.score! })), (id) => driverByDevice[id]),
    [results, driverByDevice],
  );

  const onVehiclePdf = async (r: (typeof results)[number]) => {
    const { startDate: from, endDate: to } = getValues();
    const driver = driverByDevice[r.deviceId];
    // Mes a mes (como Mapon) si el API ya la tiene; si no, por semana del periodo.
    const months = await fetchMonthlyBehavior(companyId!, r.deviceId, 6);
    const history = months.length
      ? {
          title: "Puntuación histórica (mes a mes)",
          bars: months.map((m) => ({
            label: new Date(`${m.month}-15T12:00:00`).toLocaleDateString("es-MX", { month: "short", year: "numeric" }),
            overall: m.overall,
          })),
        }
      : {
          title: "Puntuación por semana",
          bars: scoreByWeek(behaviorPointsOf(r.deviceId), settings).map((w) => ({
            label: new Date(w.start).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }),
            overall: w.overall,
          })),
        };
    downloadVehicleReportPdf({
      companyName: companies.find((c: any) => c.id === companyId)?.name ?? "",
      period: `${from.replace("T", " ")} al ${to.replace("T", " ")}`,
      vehicleName: r.vehicleName,
      driverName: driver?.name ?? null,
      score: r.score!,
      history,
      fileName: `informe_${r.vehicleName.replace(/[^\w-]+/g, "_")}_${from.split("T")[0]}.pdf`,
    });
  };
  const fmtDateTime = useMemo(() => makeDateTimeFormatter(resolveTz(tz), false), [tz]);

  const totals = useMemo(() => {
    const avg = rated.length ? rated.reduce((n, r) => n + r.score!.overall!, 0) / rated.length : null;
    const harsh = results.reduce(
      (n, r) => n + r.score!.params.harshBrake.value + r.score!.params.harshAccel.value + r.score!.params.harshTurn.value,
      0,
    );
    return { avg, best: rated[0] ?? null, worst: rated.length > 1 ? rated[rated.length - 1] : null, harsh };
  }, [rated, results]);

  const eventRows = useMemo<EventRow[]>(
    () =>
      results
        .filter((r) => !focusUnit || r.deviceId === focusUnit)
        .flatMap((r) => r.score!.events.filter((e) => kinds.has(e.kind)).map((e) => ({ r, e })))
        // Lo más reciente arriba; por marca de tiempo, no por el texto de la fecha.
        .sort((x, y) => y.e.ts - x.e.ts)
        .map(({ r, e }, i) => ({
              id: `${r.deviceId}-${e.ts}-${e.kind}-${i}`,
              ts: e.ts,
              unidad: r.vehicleName,
              evento: EVENT_LABEL[e.kind],
              fecha: fmtDateTime(e.ts),
              detalle:
                e.kind === "idle"
                  ? fmtDuration((e.durationMs ?? 0) / 1000)
                  : e.speed != null
                    ? `${Math.round(e.speed)} km/h`
                    : "",
              ubicacion: `${e.lat.toFixed(5)}, ${e.lng.toFixed(5)}`,
            })),
    [results, kinds, focusUnit, fmtDateTime],
  );

  // Mapa de la unidad elegida: su ruta (puntos ya leídos) y sus eventos, en el
  // mismo orden que la tabla de abajo (lo más reciente arriba).
  const mapData = useMemo(() => {
    if (!focusUnit) return null;
    const r = resultsState[focusUnit];
    if (!r?.score) return null;
    const events = r.score.events
      .filter((e) => kinds.has(e.kind))
      .sort((a, b) => b.ts - a.ts)
      .map((e) => ({ ...e, label: EVENT_LABEL[e.kind], when: fmtDateTime(e.ts) }));
    const route = [...behaviorPointsOf(focusUnit)].sort((a, b) => a.ts - b.ts).map((p) => ({ lat: p.lat, lng: p.lng }));
    return { route, events };
  }, [focusUnit, resultsState, kinds, fmtDateTime]);

  const exportRows = useMemo<ExportRow[]>(
    () =>
      view === "drivers"
        ? driverRows.map((d) => {
            const cell = (k: ParamKey) => (d.params[k].score == null ? "Sin dato" : `${gradeOf(d.params[k].score)} · ${fmtParam(k, Math.round(d.params[k].value * 10) / 10)}`);
            return {
              id: d.id,
              unidad: `${d.driverName} (${d.vehicles.join(", ")})`,
              general: d.overall == null ? "Sin manejo" : `${gradeOf(d.overall)} ${d.overall.toFixed(1)}`,
              eco: cell("eco"),
              ralenti: cell("idle"),
              frenados: cell("harshBrake"),
              aceleraciones: cell("harshAccel"),
              giros: cell("harshTurn"),
              excesos: cell("speeding"),
              distancia: `${d.distanceKm.toFixed(1)} km`,
              manejo: fmtDuration(d.drivingMs / 1000),
            };
          })
        : results.map((r) => {
        const s = r.score!;
        const cell = (k: ParamKey) => (s.params[k].score == null ? "Sin dato" : `${gradeOf(s.params[k].score)} · ${fmtParam(k, s.params[k].value)}`);
        return {
          id: r.deviceId,
          unidad: r.vehicleName,
          general: s.overall == null ? "Sin manejo" : `${gradeOf(s.overall)} ${s.overall.toFixed(1)}`,
          eco: cell("eco"),
          ralenti: cell("idle"),
          frenados: cell("harshBrake"),
          aceleraciones: cell("harshAccel"),
          giros: cell("harshTurn"),
          excesos: cell("speeding"),
          distancia: `${s.distanceKm.toFixed(1)} km`,
          manejo: fmtDuration(s.drivingMs / 1000),
        };
      }),
    [results, driverRows, view],
  );

  const onDownload = async (type: "pdf" | "excel") => {
    const { startDate: from, endDate: to } = getValues();
    const fileName = `comportamiento_${from.split("T")[0]}_${to.split("T")[0]}.${type === "pdf" ? "pdf" : "xlsx"}`;
    const w = PARAM_KEYS.map((k) => `${PARAM_LABEL[k]} ${settings.weights[k]}`).join(", ");
    const period =
      `Período: ${from.replace("T", " ")} al ${to.replace("T", " ")} | Eco ≤ ${settings.ecoSpeedKmh} km/h, límite ${settings.speedLimitKmh} km/h,` +
      ` ralentí > ${settings.idleMinutes} min | Pesos: ${w}`;
    setExporting(type);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const meta = { title: view === "drivers" ? "Comportamiento por conductor" : "Comportamiento del conductor", period, fileName };
      const columns = view === "drivers" ? EXPORT_COLUMNS.map((c) => (c.key === "unidad" ? { ...c, header: "Conductor (unidades)" } : c)) : EXPORT_COLUMNS;
      if (type === "excel") await exportHistoricToExcel(exportRows, columns, meta);
      else exportHistoricToPdf(exportRows, columns, meta);
    } catch {
      showAlert({ title: "Error al descargar", text: "No se pudo generar el archivo.", icon: "error", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    const now = new Date();
    setValue("startDate", toLocalInputValue(new Date(now.getTime() - 7 * DAY_MS)));
    setValue("endDate", toLocalInputValue(now));
  }, [setValue]);

  useEffect(() => () => clear(), [clear]);

  const hasResults = results.length > 0 && !loading;
  const muted = themeClasses.textSecondary;

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <div className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={() => setValue("devices", selectedDevices?.length === loadedDeviceIds.length ? [] : loadedDeviceIds)}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} hover:opacity-90 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <Controller
              name="devices"
              control={control}
              render={({ field }) => (
                <InfiniteDeviceSelect value={field.value} onChange={field.onChange} isDarkMode={isDarkMode} themeClasses={themeClasses} />
              )}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={startDate}
              endValue={endDate}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <ReportActionButton type="button" variant="secondary" onClick={() => setSettingsOpen(true)} disabled={disableWithoutCompany}>
            Ajustes
          </ReportActionButton>
          <ReportActionButton
            type="button"
            onClick={onView}
            disabled={loading || !ready || disableWithoutCompany}
            title={rangeDays > MAX_RANGE_DAYS ? `Admite hasta ${MAX_RANGE_DAYS} días` : undefined}
          >
            Calificar
          </ReportActionButton>
        </div>
      </div>

      {loading && (
        <ReportLoadingPanel isDarkMode={isDarkMode} icon="ri-steering-2-line" title="Calificando la conducción…" subtitle="Leyendo el historial y los eventos de manejo de cada unidad" />
      )}

      {hasResults && (
        <div className={`mt-4 flex flex-col gap-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {results.length} unidad(es) · eco ≤ {settings.ecoSpeedKmh} km/h · límite {settings.speedLimitKmh} km/h · ralentí &gt; {settings.idleMinutes} min
              {settingsQuery.data && !settingsQuery.data.customized && <span className={muted}> · ajustes por omisión</span>}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {(["pdf", "excel"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => onDownload(type)}
                  disabled={exporting !== null}
                  className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                >
                  {exporting === type ? "Generando..." : `Descargar ${type === "pdf" ? "PDF" : "Excel"}`}
                </button>
              ))}
              <button type="button" onClick={clear} className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}>
                Cerrar
              </button>
            </div>
          </div>

          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Puntuación media", score: totals.avg, sub: "de la flota" },
              { label: "Más eficiente", score: totals.best?.score!.overall ?? null, sub: totals.best?.vehicleName ?? "—" },
              { label: "Menos eficiente", score: totals.worst?.score!.overall ?? null, sub: totals.worst?.vehicleName ?? "—" },
            ].map((c) => (
              <div key={c.label} className={`flex items-center gap-3 p-4 rounded-xl border ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-gray-50 border-gray-200"}`}>
                <Grade score={c.score} size="lg" />
                <div className="min-w-0">
                  <p className={`text-xs ${muted}`}>{c.label}</p>
                  <p className={`text-lg font-semibold tabular-nums ${themeClasses.text}`}>{c.score == null ? "—" : c.score.toFixed(1)}</p>
                  <p className={`text-xs truncate ${muted}`}>{c.sub}</p>
                </div>
              </div>
            ))}
            <ReportStatCard icon="ri-error-warning-line" label="Eventos bruscos" value={String(totals.harsh)} isDarkMode={isDarkMode} />
          </div>

          <div role="tablist" aria-label="Agrupar por" className={`self-start flex p-0.5 rounded-lg ${isDarkMode ? "bg-white/5" : "bg-gray-100"}`}>
            {([
              ["vehicles", "Por vehículos"],
              ["drivers", "Por conductores"],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={view === id}
                onClick={() => setView(id)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${view === id ? (isDarkMode ? "bg-dark-card text-white shadow" : "bg-white text-gray-900 shadow") : muted}`}
              >
                {label}
              </button>
            ))}
          </div>

          {view === "drivers" && (
            <div className="w-full overflow-auto rounded-lg shadow max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="bg-primary-500 text-white sticky top-0 z-10">
                  <tr>
                    {["#", "Conductor", "Unidades", "Calificación", ...PARAM_KEYS.map((k) => PARAM_LABEL[k]), "Distancia", "Manejo"].map((h) => (
                      <th key={h} className="p-3 font-bold whitespace-nowrap text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {driverRows.map((d, i) => (
                    <tr key={d.id} className={`border-b ${themeClasses.cardBorder} ${themeClasses.text}`}>
                      <td className="p-3 tabular-nums">{i + 1}</td>
                      <td className={`p-3 whitespace-nowrap font-medium ${d.id === "__sin_conductor__" ? muted : ""}`}>{d.driverName}</td>
                      <td className={`p-3 max-w-56 truncate ${muted}`} title={d.vehicles.join(", ")}>{d.vehicles.join(", ")}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <Grade score={d.overall} />
                          <span className="tabular-nums">{d.overall == null ? "Sin manejo" : d.overall.toFixed(1)}</span>
                        </span>
                      </td>
                      {PARAM_KEYS.map((k) => (
                        <td key={k} className="p-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <Grade score={d.params[k].score} />
                            <span className={`tabular-nums ${d.params[k].score == null ? muted : ""}`}>
                              {d.params[k].score == null ? "Sin dato" : fmtParam(k, Math.round(d.params[k].value * 10) / 10)}
                            </span>
                          </span>
                        </td>
                      ))}
                      <td className="p-3 whitespace-nowrap tabular-nums">{d.distanceKm.toFixed(1)} km</td>
                      <td className="p-3 whitespace-nowrap tabular-nums">{fmtDuration(d.drivingMs / 1000)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={`text-[11px] px-3 py-2 ${muted}`}>
                Cada unidad cuenta para el conductor que tiene asignado hoy; la calificación del conductor pondera sus unidades por tiempo de manejo.
              </p>
            </div>
          )}

          {view === "vehicles" && (
          <div className="w-full overflow-auto rounded-lg shadow max-h-[60vh]">
            <table className="w-full text-xs">
              <thead className="bg-primary-500 text-white sticky top-0 z-10">
                <tr>
                  {["#", "Unidad", "Calificación", ...PARAM_KEYS.map((k) => PARAM_LABEL[k]), "Distancia", "Manejo", "Vel. media", "Informe"].map((h) => (
                    <th key={h} className="p-3 font-bold whitespace-nowrap text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const s = r.score!;
                  const selected = focusUnit === r.deviceId;
                  return (
                    <tr
                      key={r.deviceId}
                      onClick={() => setFocusUnit(selected ? null : r.deviceId)}
                      className={`cursor-pointer border-b ${themeClasses.cardBorder} ${selected ? (isDarkMode ? "bg-white/10" : "bg-primary-500/10") : ""} ${themeClasses.text}`}
                      title="Ver sólo los eventos de esta unidad"
                    >
                      <td className="p-3 tabular-nums">{i + 1}</td>
                      <td className="p-3 whitespace-nowrap font-medium">{r.vehicleName}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <Grade score={s.overall} />
                          <span className="tabular-nums">{s.overall == null ? "Sin manejo" : s.overall.toFixed(1)}</span>
                        </span>
                      </td>
                      {PARAM_KEYS.map((k) => (
                        <td key={k} className="p-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <Grade score={s.params[k].score} />
                            <span className={`tabular-nums ${s.params[k].score == null ? muted : ""}`}>
                              {s.params[k].score == null ? "Sin dato" : fmtParam(k, s.params[k].value)}
                            </span>
                          </span>
                        </td>
                      ))}
                      <td className="p-3 whitespace-nowrap tabular-nums">{s.distanceKm.toFixed(1)} km</td>
                      <td className="p-3 whitespace-nowrap tabular-nums">{fmtDuration(s.drivingMs / 1000)}</td>
                      <td className="p-3 whitespace-nowrap tabular-nums">{s.avgSpeed == null ? "—" : `${Math.round(s.avgSpeed)} km/h`}</td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onVehiclePdf(r);
                          }}
                          aria-label={`Informe del vehículo ${r.vehicleName} (PDF)`}
                          title="Informe del vehículo (PDF)"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-current/20 hover:text-primary-500"
                        >
                          <i className="ri-file-pdf-2-line" aria-hidden="true" /> PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <p className={`text-sm font-semibold mr-2 ${themeClasses.text}`}>
              Eventos{focusUnit ? ` · ${resultsState[focusUnit]?.vehicleName}` : ""}
            </p>
            {(Object.keys(EVENT_LABEL) as DrivingEvent["kind"][]).map((k) => {
              const on = kinds.has(k);
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    const next = new Set(kinds);
                    if (on) next.delete(k);
                    else next.add(k);
                    setKinds(next);
                  }}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary-500 text-white border-primary-500" : `${themeClasses.cardBorder} ${muted}`}`}
                >
                  {EVENT_LABEL[k]}
                </button>
              );
            })}
            {focusUnit && (
              <button type="button" onClick={() => setFocusUnit(null)} className={`text-xs underline ${muted}`}>
                Ver todas las unidades
              </button>
            )}
          </div>
          {mapData ? (
            <BehaviorEventsMap route={mapData.route} events={mapData.events} selectedTs={mapTs} />
          ) : (
            <p className={`text-xs ${muted}`}>Toca una unidad en la tabla de arriba para ver su ruta y sus eventos en el mapa.</p>
          )}
          <ReportPointsTable
            data={eventRows}
            columns={EVENT_COLUMNS}
            isDarkMode={isDarkMode}
            emptyText="Sin eventos de ese tipo en el rango."
            onRowClick={focusUnit ? (row) => setMapTs(row.ts) : undefined}
          />
        </div>
      )}

      {!hasResults && !loading && (
        <div className={`mt-4 ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-4`}>
          <div className={`flex flex-col items-center justify-center gap-3 px-6 py-10 text-center rounded-xl border border-dashed ${themeClasses.cardBorder} ${isDarkMode ? "bg-white/5" : "bg-black/[0.02]"}`}>
            <span className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${isDarkMode ? "bg-primary-500/15" : "bg-primary-500/10"}`}>
              <i className="ri-steering-2-line text-primary-500 text-2xl" />
            </span>
            <p className={`text-sm font-semibold ${themeClasses.text}`}>Comportamiento del conductor</p>
            <p className={`text-xs max-w-lg ${muted}`}>
              Califica de la A a la G cada unidad con su eco velocidad, ralentí, frenados, aceleraciones y giros bruscos y excesos. Los pesos y
              umbrales se cambian en <strong>Ajustes</strong> (hasta {MAX_RANGE_DAYS} días).
            </p>
          </div>
        </div>
      )}

      {settingsOpen && (
        <BehaviorSettingsModal
          initial={settings}
          isDarkMode={isDarkMode}
          sessionOnly={settingsQuery.data?.available === false}
          onClose={() => setSettingsOpen(false)}
          onSave={onSaveSettings}
        />
      )}
    </div>
  );
}
